/**
 * Environment Configuration — GenesisBox
 *
 * Resolution order (first match wins):
 *   1. ?apiBase= query param         (runtime override — escape hatch)
 *   2. sessionStorage apiBase        (persists override across redirects/reloads)
 *   3. Firebase Hosting host         (same-origin → routed via firebase.json rewrites)
 *   4. VITE_API_BASE_URL             (build-time override)
 *   5. DEFAULT_API_BASE              (final fallback — Firebase Hosting/API)
 *
 * Notes:
 * - Local Vite dev intentionally uses the deployed Firebase backend by
 *   default so `npm run dev` does not require local emulators.
 * - To test against the local Functions emulator, run `npm run emulator:api`
 *   and opt in with `?apiBase=http://127.0.0.1:5001/genesisbox-2261b/us-central1/genesisApi`.
 */

// ---------------------------------------------------------------------------
// Known backend URLs — extend as needed
// ---------------------------------------------------------------------------
const KNOWN_HOSTS: Record<string, string> = {
  firebase: 'https://genesisbox-2261b.web.app',
  emulator: 'http://127.0.0.1:5001/genesisbox-2261b/us-central1/genesisApi',
};

const DEFAULT_API_BASE = KNOWN_HOSTS.firebase;
const API_BASE_OVERRIDE_KEY = 'genesisbox:apiBaseOverride';

// Hostnames where the frontend is served by Firebase Hosting and the
// `genesisApi` Cloud Function is wired up via firebase.json `rewrites`
// for /auth/** and /api/**. On these hosts we always go same-origin.
const FIREBASE_HOSTING_HOSTS = new Set<string>([
  'box.genesisframeworks.com',
]);
function isFirebaseHostingHost(hostname: string): boolean {
  return (
    FIREBASE_HOSTING_HOSTS.has(hostname) ||
    hostname.endsWith('.web.app') ||
    hostname.endsWith('.firebaseapp.com')
  );
}

// ---------------------------------------------------------------------------
// Config type
// ---------------------------------------------------------------------------
export interface EnvironmentConfig {
  apiBaseUrl: string;
  isDevelopment: boolean;
  isProduction: boolean;
  mode: string;
  hostingPreset: string;
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------
function normalizeUrl(raw: string): string {
  return raw.trim().replace(/\/+$/, '');
}

function getSessionApiBaseOverride(): string {
  try {
    return typeof window !== 'undefined'
      ? window.sessionStorage.getItem(API_BASE_OVERRIDE_KEY) || ''
      : '';
  } catch {
    return '';
  }
}

function setSessionApiBaseOverride(value: string): void {
  try {
    window.sessionStorage.setItem(API_BASE_OVERRIDE_KEY, value);
  } catch {
    // sessionStorage can be blocked in private/browser-restricted contexts.
  }
}

function resolveApiBaseUrl(): string {
  // 1. Runtime override — `?apiBase=https://…` always wins. Useful for
  //    one-off testing without restarting Vite.
  if (typeof window !== 'undefined') {
    const override = new URLSearchParams(window.location.search).get('apiBase');
    if (override) {
      const normalizedOverride = normalizeUrl(override);
      setSessionApiBaseOverride(normalizedOverride);
      return normalizedOverride;
    }

    // 2. Preserve runtime override after React navigation, auth redirects,
    //    or a reload on /drive without query params.
    const sessionOverride = getSessionApiBaseOverride();
    if (sessionOverride) return normalizeUrl(sessionOverride);

    // 3. Hosted on Firebase → call same-origin so /auth/** and /api/**
    //    flow through the firebase.json rewrites to the genesisApi function.
    if (isFirebaseHostingHost(window.location.hostname)) {
      return normalizeUrl(window.location.origin);
    }
  }
  // 3. Build-time env var — use this for explicit local/staging overrides.
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) return normalizeUrl(envUrl);
  // 4. Fallback, including localhost/127.0.0.1 dev.
  return DEFAULT_API_BASE;
}

function detectPreset(url: string): string {
  const clean = url.replace(/\/+$/, '');
  for (const [key, base] of Object.entries(KNOWN_HOSTS)) {
    if (clean === base) return key;
  }
  return 'custom';
}

// ---------------------------------------------------------------------------
// Exported singleton
// ---------------------------------------------------------------------------
const resolvedUrl = resolveApiBaseUrl();

export const config: EnvironmentConfig = {
  apiBaseUrl: resolvedUrl,
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE,
  hostingPreset: detectPreset(resolvedUrl),
};

export function validateEnvironment(): void {
  if (!config.apiBaseUrl) {
    console.warn('[GenesisBox] API base URL is not configured');
  }
  console.log(`[GenesisBox] Mode: ${config.mode}`);
  console.log(`[GenesisBox] API: ${config.apiBaseUrl}`);
  console.log(`[GenesisBox] Hosting: ${config.hostingPreset}`);
}
